import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { getProduct, formatPrice } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Seo } from "@/components/Seo";
import NotFound from "@/pages/NotFound";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/motion";
import { FALLBACK_IMG } from "@/lib/images";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ChevronRight, Gift, Heart, Minus, PackageCheck, Plus, ShieldCheck, ShoppingBag, Star } from "lucide-react";

export default function Product() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [giftWrap, setGiftWrap] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(false);
  const ctaRef = useRef(null);
  const { addToCart, toggleFavorite, favorites, setCartOpen } = useStore();

  useEffect(() => {
    setProduct(null);
    setLoading(true);
    setNotFound(false);
    setImgIndex(0);
    setQty(1);
    setGiftWrap(false);
    getProduct(slug)
      .then((p) => {
        setProduct(p);
        setVariant(p.variants?.length === 1 ? p.variants[0] : null);
      })
      .catch((err) => {
        setProduct(null);
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!ctaRef.current) return;
    const obs = new IntersectionObserver(([e]) => setStickyVisible(!e.isIntersecting), { threshold: 0 });
    obs.observe(ctaRef.current);
    return () => obs.disconnect();
  }, [product]);

  if (loading) {
    return <div data-testid="product-loading" className="container-shule py-24 text-center text-shule-soft font-serif text-2xl">Yükleniyor…</div>;
  }
  if (notFound || !product) return <NotFound />;

  const fav = favorites.includes(product.slug);
  const outOfStock = product.stockStatus === "out_of_stock" || product.stock === 0 || product.stock_quantity === 0;
  const images = product.images?.length ? product.images : [FALLBACK_IMG];
  const productUrl = `https://www.shuletaki.com.tr/urun/${product.slug}`;
  const availability = product.stockStatus === "out_of_stock"
    ? "https://schema.org/OutOfStock"
    : "https://schema.org/InStock";
  const jsonLd = [
    {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.seo_description || product.short || product.description,
    image: images,
    sku: product.sku,
    url: productUrl,
    brand: { "@type": "Brand", name: "Shule" },
    offers: {
      "@type": "Offer",
      price: product.price,
      priceCurrency: "TRY",
      availability,
      url: productUrl,
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: "Shule Tasarım Aksesuar", url: "https://www.shuletaki.com.tr/" },
    },
    ...(product.rating && product.reviews_count ? {
      aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating, reviewCount: product.reviews_count }
    } : {}),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://www.shuletaki.com.tr/" },
        { "@type": "ListItem", position: 2, name: product.category_name || product.category, item: `https://www.shuletaki.com.tr/kategori/${product.category}` },
        { "@type": "ListItem", position: 3, name: product.name, item: productUrl },
      ],
    },
  ];

  const handleAdd = () => {
    if (outOfStock) {
      toast.error("Bu ürün şu anda stokta yok");
      return;
    }
    if (product.variants?.length > 1 && !variant) {
      toast.error("Lütfen bir varyant seçin");
      return;
    }
    addToCart(product, variant, qty, giftWrap);
    setCartOpen(true);
  };

  const addPair = () => {
    addToCart(product, variant, 1, false, true);
    (product.pair_with || []).forEach((p) => addToCart(p, p.variants?.[0], 1, false, true));
    toast.success("Kombin tamamı sepete eklendi");
    setCartOpen(true);
  };

  return (
    <>
      <Seo title={product.seo_title || product.name} description={product.seo_description || product.short || product.description} jsonLd={jsonLd} path={`/urun/${product.slug}`} image={images[0]} type="product" />
      <div className="container-shule py-8 lg:py-14">
        <nav className="flex items-center gap-1.5 text-xs text-shule-soft mb-6" aria-label="Sayfa yolu">
          <Link to="/" className="hover:text-shule-goldhover min-h-[24px] inline-flex items-center">Ana Sayfa</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/kategori/${product.category}`} className="hover:text-shule-goldhover">{product.category_name || product.category}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-shule-mocha">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <Reveal className="relative">
              <div className="rounded-t-[140px] rounded-b-[2rem] overflow-hidden border border-shule-line bg-shule-cream">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={imgIndex}
                    data-testid="product-main-image"
                    src={images[imgIndex]}
                    alt={product.name}
                    onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                    initial={{ opacity: 0, scale: 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.45 }}
                    className="w-full aspect-[4/5] object-cover"
                  />
                </AnimatePresence>
              </div>
              {product.is_demo && <span className="absolute top-4 left-4 badge-tag">Demo ürün</span>}
            </Reveal>
            <div className="mt-4 flex gap-3 flex-wrap">
              {images.slice(0, 4).map((img, i) => (
                <button key={i} data-testid={`gallery-thumb-${i}`} onClick={() => setImgIndex(i)}
                  className={`w-20 h-24 rounded-2xl overflow-hidden border-2 transition-colors ${i === imgIndex ? "border-shule-gold" : "border-shule-line"}`}
                  aria-label={`Görsel ${i + 1}`}>
                  <img src={img} alt={product.name} loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div>
            <Reveal>
              <p className="eyebrow">Shule Seçkisi</p>
              <h1 data-testid="product-title" className="mt-2 font-serif text-4xl sm:text-5xl text-shule-coffee text-balance">{product.name}</h1>
              {product.rating && product.reviews_count ? (
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <Star className="w-4 h-4 fill-shule-gold text-shule-gold" />
                  <span className="font-semibold">{product.rating}</span>
                  <span className="text-shule-soft">({product.reviews_count} değerlendirme)</span>
                </div>
              ) : null}
              <div className="mt-4 flex items-baseline gap-3">
                <span data-testid="product-price" className="font-serif text-4xl">{formatPrice(product.price)}</span>
                {product.compare_at && <span className="text-shule-soft line-through">{formatPrice(product.compare_at)}</span>}
              </div>
              <p className="mt-5 text-shule-mocha leading-relaxed">{product.description}</p>
            </Reveal>

            <Reveal delay={0.06} className="mt-6 grid grid-cols-3 gap-2">
              {[
                { icon: ShieldCheck, label: "Güvenli ödeme" },
                { icon: Gift, label: "Hediye seçeneği" },
                { icon: PackageCheck, label: outOfStock ? "Stokta yok" : "Satışa hazır" },
              ].map((b) => (
                <div key={b.label} className="flex flex-col items-center gap-1.5 rounded-2xl border border-shule-line bg-shule-paper py-3 px-2 text-center">
                  <b.icon className="w-4 h-4 text-shule-gold" />
                  <span className="text-[11px] font-medium text-shule-mocha leading-tight">{b.label}</span>
                </div>
              ))}
            </Reveal>

            <Reveal delay={0.1} className="mt-7">
              <p className="text-sm font-semibold mb-3">Varyant: <span className="text-shule-soft font-normal">{variant?.label}</span></p>
              <div className="flex flex-wrap gap-2">
                {product.variants?.map((v) => (
                  <button key={v.id} data-testid={`variant-${v.id}`} onClick={() => setVariant(v)}
                    className={`min-h-[44px] px-5 rounded-full border text-sm font-medium transition-colors ${variant?.id === v.id ? "bg-shule-coffee text-shule-paper border-shule-coffee" : "border-shule-lineaccent hover:border-shule-gold"}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.15} className="mt-7">
              <div className="flex items-center justify-between rounded-2xl border border-shule-line bg-shule-paper px-5 py-4">
                <span className="inline-flex items-center gap-2 text-sm font-medium">
                  <Gift className="w-4 h-4 text-shule-goldhover" /> Özel hediye kutusu ve not kartı
                </span>
                <Switch data-testid="gift-wrap-toggle" checked={giftWrap} onCheckedChange={setGiftWrap} aria-label="Hediye paketi" />
              </div>
            </Reveal>

            <Reveal delay={0.2} className="mt-7">
              <div ref={ctaRef} className="flex items-stretch gap-3">
                <div className="flex items-center border border-shule-lineaccent rounded-full">
                  <button data-testid="qty-decrease-button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-full min-h-[52px] flex items-center justify-center" aria-label="Azalt">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span data-testid="qty-value" className="w-8 text-center font-bold">{qty}</span>
                  <button data-testid="qty-increase-button" onClick={() => setQty((q) => q + 1)} className="w-11 h-full min-h-[52px] flex items-center justify-center" aria-label="Artır">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <button data-testid="add-to-cart-button" onClick={handleAdd} disabled={outOfStock}
                  className="btn-primary-shule flex-1 !min-h-[52px] disabled:opacity-50 disabled:cursor-not-allowed">
                  <ShoppingBag className="w-4 h-4" /> {outOfStock ? "Stokta Yok" : "Sepete Ekle"}
                </button>
                <button data-testid="product-favorite-button" onClick={() => toggleFavorite(product.slug)}
                  className={`w-[52px] min-h-[52px] rounded-full border flex items-center justify-center transition-colors ${fav ? "border-shule-gold bg-shule-goldlight/40" : "border-shule-lineaccent hover:border-shule-gold"}`}
                  aria-label="Favorilere ekle">
                  <Heart className={`w-5 h-5 ${fav ? "fill-shule-gold text-shule-gold" : ""}`} />
                </button>
              </div>
            </Reveal>

            <Reveal delay={0.25} className="mt-8">
              <Accordion type="single" collapsible>
                <AccordionItem value="materials">
                  <AccordionTrigger data-testid="accordion-materials" className="text-sm font-semibold min-h-[44px]">Malzeme & İçerik</AccordionTrigger>
                  <AccordionContent className="text-sm text-shule-mocha">{product.materials}</AccordionContent>
                </AccordionItem>
                <AccordionItem value="care">
                  <AccordionTrigger data-testid="accordion-care" className="text-sm font-semibold min-h-[44px]">Bakım Önerileri</AccordionTrigger>
                  <AccordionContent className="text-sm text-shule-mocha">
                    {product.care || "Ürünün formunu ve yüzeyini korumak için parfüm, kimyasal ürünler ve suyla doğrudan temastan kaçının; kullanmadığınızda kuru ve kapalı şekilde saklayın."}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="shipping">
                  <AccordionTrigger data-testid="accordion-shipping" className="text-sm font-semibold min-h-[44px]">Kargo & İade</AccordionTrigger>
                  <AccordionContent className="text-sm text-shule-mocha">
                    Siparişinize uygulanacak güncel teslimat ve iade koşulları sepet ve ödeme adımında gösterilir.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </Reveal>
          </div>
        </div>

        {product.pair_with?.length > 0 && (
          <section data-testid="pair-with-section" className="mt-20 lg:mt-28">
            <Reveal className="flex flex-wrap items-end justify-between gap-4 mb-8">
              <div>
                <p className="eyebrow">Bununla Kombinle</p>
                <h2 className="mt-2 font-serif text-3xl text-shule-coffee">Birlikte daha güzel</h2>
              </div>
              <button data-testid="pair-add-all-button" onClick={addPair} className="btn-outline-shule">
                Seti Sepete Ekle
              </button>
            </Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              <ProductCard product={product} />
              {product.pair_with.map((p, i) => <ProductCard key={p.slug} product={p} index={i + 1} />)}
            </div>
          </section>
        )}
      </div>

      <AnimatePresence>
        {stickyVisible && (
          <motion.div
            data-testid="sticky-add-to-cart-bar"
            initial={{ y: 90 }}
            animate={{ y: 0 }}
            exit={{ y: 90 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-16 lg:bottom-0 inset-x-0 z-40 bg-shule-paper/95 backdrop-blur-md border-t border-shule-line"
          >
            <div className="container-shule flex items-center gap-3 py-3">
              <img src={images[0]} alt={product.name} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                className="w-11 h-12 rounded-xl object-cover border border-shule-line" />
              <div className="flex-1 min-w-0">
                <p className="font-serif text-base leading-tight truncate">{product.name}</p>
                <p className="text-xs text-shule-soft">{variant?.label} · <span className="font-bold text-shule-coffee">{formatPrice(product.price)}</span></p>
              </div>
              <button data-testid="sticky-add-to-cart-button" onClick={handleAdd} disabled={outOfStock}
                className="btn-primary-shule !px-6 disabled:opacity-50 disabled:cursor-not-allowed">
                {outOfStock ? "Stokta Yok" : "Sepete Ekle"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
